//
//
//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { ActivityEnum, AlertLevelEnum, MESSAGE_TO_SIGN, RoleEnum, WalletEnum } from '@shared/Enums'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'

import { dispatchError, OtStoreState } from '@react/redux/store'

import { alertActions } from '@react/redux/alertSlice'
import { ETH_ADDR_ZERO, EthAddr } from '@shared/SchemaTypes'

import { chainActions, ChainState } from '@react/redux/chainSlice'

import { CompanyProfileCTO } from '@shared/CompanyProfileCTO'
import { wsServerActionAccountSubscribeAction } from '@shared/WsServerActions'
import { Contract, ethers, Network, Signer } from 'ethers'

import { ServiceConfigurationAbi } from '@shared/Abis'

import { PerimeterChainAddressesInterface } from '@react/utils/PerimeterAddresses'
import { ChainConfiguration, UserConnection } from '@shared/DeploymentConfigs'
import { PoolBaseOverviewCTO } from '@shared/sharedTypes'
import axios from 'axios'
import { ServiceConfigurationV5 } from '@typechain-types/contracts'
import { ChainAccountValues } from '@shared/LiquidityAsset'
import { getRequiredAddress, otAssert } from '@shared/conversions'
import { poolActions } from '@react/redux/poolSlice'
import {
  fetchPoolAccountAccessPairRegenerationLocalHostOnly,
  fetchPoolAccountAccessPairs,
} from '@react/fetchers/PoolFetchers'

export const loginToChainThunk =
  (
    signer: Signer,
    walletEnum: WalletEnum,
    roleEnumDefault: RoleEnum
  ): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    await loginToChainFunction(
      dispatch,
      getState().poolState.poolOverviewCTOs,
      getState().chainState,
      signer,
      walletEnum,
      roleEnumDefault
    )
  }
export async function loginToChainFunction(
  dispatch: ThunkDispatch<OtStoreState, unknown, UnknownAction>,
  poolOverviewCTOs: Record<EthAddr, PoolBaseOverviewCTO>,
  chainState: ChainState,
  signer: Signer,
  walletEnum: WalletEnum,
  roleEnumDefault: RoleEnum
): Promise<void> {
  const activity = ActivityEnum.LOGIN
  try {
    const { chainConfiguration, stageConfiguration, webSocket } = chainState

    const serverApiBaseUrl = stageConfiguration.serverApiBaseUrl
    const restAPIKey = stageConfiguration.restAPIKey
    const chainConfigurationName = chainConfiguration.chainConfigurationName
    const perimeterAddresses = chainConfiguration.perimeterAddresses
    const accountAddr: EthAddr = getRequiredAddress(await signer.getAddress())
    logOT.info('loginToChainThunk  accountAddr', accountAddr)
    otAssert(serverApiBaseUrl, 'serverApiBaseUrl null')
    otAssert(perimeterAddresses, 'perimeterAddresses null')
    otAssert(chainConfigurationName, 'chainConfigurationName null')
    localStorage.setItem('walletType', walletEnum)

    const companyProfile = await companyProfileGetForAccount(serverApiBaseUrl, accountAddr)

    const accountBalances: ChainAccountValues = {}
    perimeterAddresses.liquidityAssets.forEach((liquidityAsset) => {
      accountBalances[liquidityAsset.addr] = '0'
    })
    const roleEnum =
      roleEnumDefault === RoleEnum.TO_BE_DETERMINED
        ? await determineRoleFromAccountAddr(perimeterAddresses, signer, accountAddr, poolOverviewCTOs)
        : roleEnumDefault

    logOT.info('loginToChainThunk ', roleEnum)
    let signedMessage = undefined
    if ((!companyProfile.uuid || companyProfile.uuid.length == 0) && location.hostname.startsWith('app')) {
      console.log('loginToChainThunk signer.signMessage')
      try {
        signedMessage = await signer.signMessage(MESSAGE_TO_SIGN)
      } catch (e) {
        logOT.info('loginToChainThunk no signed message signer.signMessage')
        signedMessage = undefined
      }
    }
    logOT.info('loginToChainThunk signedMessage', accountAddr, signedMessage)
    const userConnection: UserConnection = {
      accountAddr,
      walletEnum,
      signer,
      roleEnum,
      companyProfile,
      accountBalances,
      signedMessage,
    }
    logOT.info('loginToChainThunk userConnection AppKitListener', userConnection)

    await dispatch(chainActions.loginUser(userConnection))
    logOT.info('loginToChainThunk loginUser AppKitListener', userConnection)
    logOT.info(
      'loginToChainThunk wsServerActionAccountSubscribeAction accountAddr',
      !!webSocket,
      chainConfigurationName,
      accountAddr
    )
    if (stageConfiguration.stageName === 'local') {
      await fetchPoolAccountAccessPairRegenerationLocalHostOnly(serverApiBaseUrl, restAPIKey)
    }
    const poolAccountAccessPairs = await fetchPoolAccountAccessPairs(serverApiBaseUrl, restAPIKey, accountAddr)
    dispatch(poolActions.addPoolAccountAccessPairs({ poolAccountAccessPairs }))
    await dispatch(alertActions.clearAlerts())
    webSocket?.send(wsServerActionAccountSubscribeAction(accountAddr, chainConfigurationName))
  } catch (e: any) {
    logOT.error('loginToChainThunk error', e)
    dispatch(
      alertActions.addAlert({
        title: 'Could Not Login',
        description: 'Could Not Login',
        createdAt: Date.now() / 1000,
        dueDateTime: Date.now() / 1000,
        txHash: '',
        sourceObjectAddr: ETH_ADDR_ZERO,
        alertLevel: AlertLevelEnum.ERROR,
      })
    )
    await dispatchError(dispatch, e, activity, 1057)
  }
}

export const loginDirectToChainThunk =
  (userConnection: UserConnection): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const activity = ActivityEnum.LOGIN
    try {
      const { stageConfiguration, chainConfiguration, webSocket } = getState().chainState

      const serverApiBaseUrl = stageConfiguration.serverApiBaseUrl
      const restAPIKey = stageConfiguration.restAPIKey
      const chainConfigurationName = chainConfiguration.chainConfigurationName
      const providerReadUrl = chainConfiguration.frontendProviderUrl
      const perimeterAddresses = chainConfiguration.perimeterAddresses
      otAssert(perimeterAddresses, 'perimeterAddresses null')

      const signer = new ethers.Wallet(userConnection.signerKey as string, new ethers.JsonRpcProvider(providerReadUrl))

      const accountAddr: EthAddr = getRequiredAddress(await signer.getAddress())
      const walletEnum = userConnection.walletEnum

      localStorage.setItem('walletType', walletEnum)

      const companyProfile = await companyProfileGetForAccount(serverApiBaseUrl, accountAddr)

      logOT.debug('loginToChainThunk companyProfile', companyProfile)

      const accountBalances: ChainAccountValues = {}
      perimeterAddresses.liquidityAssets.forEach((liquidityAsset) => {
        accountBalances[liquidityAsset.addr] = '0'
      })

      const roleEnum = userConnection.roleEnum

      const newUserConnection: UserConnection = {
        accountAddr,
        walletEnum,
        signer,
        roleEnum,
        companyProfile,
        accountBalances,
      }
      dispatch(alertActions.clearAlerts())
      dispatch(chainActions.loginUser(newUserConnection))
      // await fetchPoolAccountAccessPairRegeneration(serverApiBaseUrl, restAPIKey)
      const poolAccountAccessPairs = await fetchPoolAccountAccessPairs(serverApiBaseUrl, restAPIKey, accountAddr)
      dispatch(poolActions.addPoolAccountAccessPairs({ poolAccountAccessPairs }))

      logOT.debug(
        'loginToChainThunkDirect wsServerActionAccountSubscribeAction',
        !!webSocket,
        roleEnum,
        accountAddr,
        chainConfigurationName
      )
      webSocket?.send(wsServerActionAccountSubscribeAction(accountAddr, chainConfigurationName))
      logOT.debug('loginToChainThunk Sent on websocket', !!webSocket, roleEnum, accountAddr, chainConfigurationName)
    } catch (e: any) {
      logOT.error('loginToChainThunk error', e)
      dispatch(
        alertActions.addAlert({
          title: 'Could Not Login',
          description: 'Could Not Login',
          createdAt: Date.now() / 1000,
          dueDateTime: Date.now() / 1000,
          txHash: '',
          sourceObjectAddr: ETH_ADDR_ZERO,
          alertLevel: AlertLevelEnum.ERROR,
        })
      )
      await dispatchError(dispatch, e, activity, 1057)
    }
  }

export const companyProfileGetForAccount = async (
  serverApiBaseUrl: string,
  accountAddr: string
): Promise<CompanyProfileCTO> => {
  try {
    logOT.debug('companyProfileGetForAccount', serverApiBaseUrl, accountAddr)

    const r = await axios.get(`${serverApiBaseUrl}/companyProfileForAccount/${accountAddr}`)
    logOT.debug('companyProfileGetForAccount r', r)
    const data = r.data
    logOT.debug('companyProfileGet', data)
    let companyProfileCTO: CompanyProfileCTO = data.results
    if (
      !companyProfileCTO ||
      (Array.isArray(companyProfileCTO) && (data.results as unknown as string[]).length === 0)
    ) {
      companyProfileCTO = {
        uuid: '',
        companyName: 'Unregistered',
        wallets: [accountAddr],
        emails: [],
      }
    }
    return companyProfileCTO
  } catch (e) {
    logOT.error('companyProfileGetForAccount', e)
    return {
      uuid: '',
      companyName: 'Unregistered',
      wallets: [accountAddr],
      emails: [],
    }
  }
}
async function isOperator(
  perimeterAddresses: PerimeterChainAddressesInterface,
  signer: Signer,
  accountAddr: EthAddr
): Promise<boolean> {
  const serviceConfiguration = new Contract(
    perimeterAddresses.ServiceConfiguration,
    ServiceConfigurationAbi,
    signer
  ) as unknown as ServiceConfigurationV5
  const operatorRole = await serviceConfiguration.OPERATOR_ROLE()

  const isOpp = await serviceConfiguration.hasRole(operatorRole, accountAddr)

  return isOpp
}
async function isPoolAdminVerified(
  perimeterAddresses: PerimeterChainAddressesInterface,
  signer: Signer,
  accountAddr: EthAddr
): Promise<boolean> {
  const serviceConfiguration = new Contract(perimeterAddresses.ServiceConfiguration, ServiceConfigurationAbi, signer)
  const poolAdminRole = await serviceConfiguration.POOL_ADMIN_ROLE()

  const isPoolAdmin = await serviceConfiguration.hasRole(poolAdminRole, accountAddr)

  return isPoolAdmin
}

function _addSignerToConfiguration(userConnection: UserConnection, chainConfiguration: ChainConfiguration): void {
  userConnection.signer = new ethers.Wallet(
    userConnection.signerKey as string,
    new ethers.JsonRpcProvider(
      chainConfiguration.frontendProviderUrl,
      new Network(chainConfiguration.chainName, chainConfiguration.chainId),
      {
        staticNetwork: true,
        pollingInterval: 100,
        polling: false,
        batchMaxCount: 1,
      }
    )
  )
}

const determineRoleFromAccountAddr = async (
  perimeterAddresses: PerimeterChainAddressesInterface,
  signer: Signer,
  accountAddr: EthAddr,
  pools: Record<EthAddr, PoolBaseOverviewCTO>
): Promise<RoleEnum> => {
  const poolValues = Object.values(pools)
  // if (poolValues.length === 0) {
  logOT.info('  PerimeterAddresses.PoolAccessControl', accountAddr)
  try {
    const isOpp = await isOperator(perimeterAddresses, signer, accountAddr)
    if (isOpp) {
      return RoleEnum.OPERATOR
    }
    const isPoolAdmin = await isPoolAdminVerified(perimeterAddresses, signer, accountAddr)
    logOT.info('  PerimeterAddresses.PoolAccessControl isPoolAdmin', isPoolAdmin, accountAddr)
    if (isPoolAdmin) {
      return RoleEnum.POOL_ADMIN
    }
  } catch (e) {
    logOT.error('  PerimeterAddresses.PoolAccessControl', accountAddr, e)
  }
  // }

  for (let i = 0; i < poolValues.length; i++) {
    const pool = poolValues[i]
    if (pool.borrowerManagerAddr === accountAddr) {
      return RoleEnum.BORROWER
    }
    if (pool.poolAdminAddr === accountAddr) {
      return RoleEnum.POOL_ADMIN
    }
  }
  return RoleEnum.LENDER
}
